// 取引先マスタ管理（DEC-051 / S-052-1d）
// マスタの正規化オーナーとしてのメンテ画面。リネーム・統合（マージ）・削除を行う。
// 統合は表記ゆれ（「ゼネコン」「元請」→「A建設」）を 1 レコードへ収束させ、
// 紐づく議事録の companyIds も付け替える（自己治癒の手動版）。
import React, { useMemo, useState } from 'react';
import { User } from 'firebase/auth';
import {
  collection, deleteDoc, doc, getDocs, query, serverTimestamp, updateDoc, where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { Btn, Card, Icon } from '../components/primitives';
import { Company, useCompanies } from '../useCompanies';

interface Props {
  user: User;
  onBack: () => void;
}

// 指定ユーザーの全プロジェクトの meetings から fromId を toId へ付け替える
// （toId が null のときは fromId を取り除くだけ）。
async function repointCompanyInMeetings(uid: string, fromId: string, toId: string | null) {
  const projSnap = await getDocs(
    query(collection(db, 'projects'), where('members', 'array-contains', uid))
  );
  for (const proj of projSnap.docs) {
    const meetingsRef = collection(db, 'projects', proj.id, 'meetings');
    const mSnap = await getDocs(query(meetingsRef, where('companyIds', 'array-contains', fromId)));
    for (const m of mSnap.docs) {
      const ids: string[] = Array.isArray((m.data() as any).companyIds) ? (m.data() as any).companyIds : [];
      let next = ids.filter((id) => id !== fromId);
      if (toId && !next.includes(toId)) next = [...next, toId];
      await updateDoc(doc(db, 'projects', proj.id, 'meetings', m.id), { companyIds: next });
    }
  }
}

const CompanyMasterPage: React.FC<Props> = ({ user, onBack }) => {
  const { companies, loading } = useCompanies(user.uid);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState('');

  // 統合モード
  const [mergeMode, setMergeMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mergeTarget, setMergeTarget] = useState<string | null>(null);

  const selectedCompanies = useMemo(
    () => companies.filter((c) => selected.has(c.id)),
    [companies, selected]
  );

  const startRename = (c: Company) => {
    setEditingId(c.id);
    setEditName(c.name);
  };

  const saveRename = async (c: Company) => {
    const name = editName.trim();
    if (!name || name === c.name) { setEditingId(null); return; }
    setBusy(true);
    setBusyMsg('リネーム中...');
    try {
      await updateDoc(doc(db, 'users', user.uid, 'companies', c.id), {
        name: name.substring(0, 100),
        updatedAt: serverTimestamp(),
      });
      setEditingId(null);
    } catch (e: any) {
      alert('リネームに失敗しました: ' + (e.message || e));
    } finally {
      setBusy(false);
      setBusyMsg('');
    }
  };

  const deleteCompany = async (c: Company) => {
    if (!window.confirm(`「${c.name}」を削除しますか？\nこの相手先に紐づいた議事録は「未分類」に戻ります。`)) return;
    setBusy(true);
    setBusyMsg('削除中...');
    try {
      await repointCompanyInMeetings(user.uid, c.id, null);
      await deleteDoc(doc(db, 'users', user.uid, 'companies', c.id));
    } catch (e: any) {
      alert('削除に失敗しました: ' + (e.message || e));
    } finally {
      setBusy(false);
      setBusyMsg('');
    }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
    // 統合先が選択解除されたら統合先もクリアする
    if (mergeTarget && !next.has(mergeTarget)) setMergeTarget(null);
  };

  const runMerge = async () => {
    if (!mergeTarget || selected.size < 2) return;
    const target = companies.find((c) => c.id === mergeTarget);
    if (!target) return;
    const sources = selectedCompanies.filter((c) => c.id !== mergeTarget);
    if (sources.length === 0) return;
    if (!window.confirm(`${sources.map((s) => `「${s.name}」`).join('')}を「${target.name}」に統合しますか？`)) return;

    setBusy(true);
    try {
      // 1. 各ソースの議事録を target へ付け替え
      for (let i = 0; i < sources.length; i++) {
        setBusyMsg(`統合中... (${i + 1}/${sources.length})`);
        await repointCompanyInMeetings(user.uid, sources[i].id, target.id);
      }
      // 2. ソース名・別名を target の aliases へ集約（表記ゆれ吸収）
      const aliasSet = new Set<string>(target.aliases || []);
      for (const s of sources) {
        aliasSet.add(s.name);
        (s.aliases || []).forEach((a) => aliasSet.add(a));
      }
      aliasSet.delete(target.name);
      setBusyMsg('別名を集約中...');
      await updateDoc(doc(db, 'users', user.uid, 'companies', target.id), {
        aliases: Array.from(aliasSet).slice(0, 30),
        updatedAt: serverTimestamp(),
      });
      // 3. ソースを削除
      for (const s of sources) {
        setBusyMsg('重複を削除中...');
        await deleteDoc(doc(db, 'users', user.uid, 'companies', s.id));
      }
      setMergeMode(false);
      setSelected(new Set());
      setMergeTarget(null);
    } catch (e: any) {
      alert('統合に失敗しました: ' + (e.message || e));
    } finally {
      setBusy(false);
      setBusyMsg('');
    }
  };

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: '20px 24px 96px' }}>
      <button onClick={onBack} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
        color: 'var(--fg-tertiary)', font: 'var(--type-ui)', cursor: 'pointer', marginBottom: 16, padding: 0,
      }}>
        <Icon name="arrow-left" size={16} />案件一覧に戻る
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h1 style={{ font: 'var(--type-h2)', color: 'var(--fg)', marginBottom: 4 }}>相手先の管理</h1>
          <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
            {companies.length}件 · リネーム・統合・削除で表記ゆれを整理できます
          </p>
        </div>
        {companies.length >= 2 && (
          <Btn kind={mergeMode ? 'ghost' : 'accent'} size="sm" icon="git-compare"
            onClick={() => { setMergeMode((v) => !v); setSelected(new Set()); setMergeTarget(null); }}>
            {mergeMode ? '統合をやめる' : '統合する'}
          </Btn>
        )}
      </div>

      {busy && (
        <div style={{ padding: '10px 14px', borderRadius: 'var(--r-2)', background: 'var(--accent-fill-faint)', border: '1px solid var(--border-accent)', color: 'var(--teal-300)', font: 'var(--type-caption)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="loader" size={14} spin />{busyMsg || '処理中...'}
        </div>
      )}

      {/* 統合の操作バー */}
      {mergeMode && selected.size > 0 && (
        <Card pad={14} style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ font: 'var(--type-caption)', color: 'var(--fg-secondary)' }}>
            {selected.size}件を選択中 · 統合先:
          </span>
          <select value={mergeTarget || ''} onChange={(e) => setMergeTarget(e.target.value || null)}
            style={{
              padding: '6px 10px', borderRadius: 'var(--r-2)', background: 'var(--bg-elevated)',
              border: '1px solid var(--border-strong)', color: 'var(--fg)', font: 'var(--type-caption)',
            }}>
            <option value="">選択してください</option>
            {selectedCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div style={{ flex: 1 }} />
          <Btn kind="cta" size="sm" icon="check" disabled={!mergeTarget || selected.size < 2 || busy} onClick={runMerge}>
            統合を実行
          </Btn>
        </Card>
      )}

      {loading && <div style={{ textAlign: 'center', color: 'var(--fg-tertiary)', padding: 48, font: 'var(--type-body)' }}>読み込み中...</div>}

      {!loading && companies.length === 0 && (
        <Card style={{ textAlign: 'center', padding: 48 }}>
          <Icon name="building" size={28} style={{ color: 'var(--fg-tertiary)', marginBottom: 10 }} />
          <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)' }}>取引先がまだありません</p>
          <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginTop: 4 }}>
            議事録を作成すると、登場した相手先が自動で追加されます
          </p>
        </Card>
      )}

      {!loading && companies.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {companies.map((c) => {
            const isEditing = editingId === c.id;
            const isSelected = selected.has(c.id);
            return (
              <Card key={c.id} pad={14}
                hoverable={mergeMode}
                onClick={mergeMode ? () => toggleSelect(c.id) : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  border: isSelected ? '1px solid var(--border-accent)' : undefined,
                  background: isSelected ? 'var(--accent-fill-faint)' : undefined,
                }}>
                {mergeMode && (
                  <div style={{
                    width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                    border: '1.5px solid ' + (isSelected ? 'var(--teal-400)' : 'var(--border-strong)'),
                    background: isSelected ? 'var(--teal-400)' : 'transparent',
                    display: 'grid', placeItems: 'center',
                  }}>
                    {isSelected && <Icon name="check" size={12} style={{ color: 'var(--bg)' }} />}
                  </div>
                )}
                <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--accent-fill)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  <Icon name="building" size={15} style={{ color: 'var(--teal-300)' }} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {isEditing ? (
                    <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveRename(c); if (e.key === 'Escape') setEditingId(null); }}
                      maxLength={100}
                      style={{
                        width: '100%', padding: '6px 10px', borderRadius: 'var(--r-2)',
                        background: 'var(--bg-hover)', border: '1px solid var(--border-accent)',
                        color: 'var(--fg)', font: 'var(--type-ui)', outline: 'none',
                      }} />
                  ) : (
                    <>
                      <div style={{ font: '600 15px/1.3 var(--font-sans)', color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.name}
                      </div>
                      <div style={{ display: 'flex', gap: 10, marginTop: 2, flexWrap: 'wrap' }}>
                        {c.kind && <span style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>{c.kind}</span>}
                        {typeof c.meetingCount === 'number' && c.meetingCount > 0 && (
                          <span style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>{c.meetingCount}件</span>
                        )}
                        {(c.aliases && c.aliases.length > 0) && (
                          <span style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
                            別名: {c.aliases.slice(0, 3).join('・')}{c.aliases.length > 3 ? '…' : ''}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {!mergeMode && (
                  isEditing ? (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <Btn kind="cta" size="sm" icon="check" disabled={busy} onClick={() => saveRename(c)}>保存</Btn>
                      <Btn kind="ghost" size="sm" onClick={() => setEditingId(null)}>取消</Btn>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button onClick={() => startRename(c)} disabled={busy} title="リネーム"
                        style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 'var(--r-2)', padding: '5px 8px', cursor: 'pointer', color: 'var(--fg-secondary)', display: 'inline-flex' }}>
                        <Icon name="align-left" size={14} />
                      </button>
                      <button onClick={() => deleteCompany(c)} disabled={busy} title="削除"
                        style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 'var(--r-2)', padding: '5px 8px', cursor: 'pointer', color: 'var(--danger)', display: 'inline-flex' }}>
                        <Icon name="x" size={14} />
                      </button>
                    </div>
                  )
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CompanyMasterPage;
